import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import type { AcceptanceRegisterResponseRow } from '@risksmart-app/drizzle/src/dto';
import type {
  Acceptance_Bool_Exp,
  GetAcceptancesQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetAcceptancesDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

const useAcceptancesGraphqlVariables = () => {
  const whereFilter = useEntityWhereFilter<Acceptance_Bool_Exp>(
    Parent_Type_Enum.Acceptance
  );

  return { where: whereFilter };
};

/**
 * Maps TRPC acceptance data to match the GraphQL query structure
 */
export function mapTrpcAcceptancesToGraphQL(
  trpcData: AcceptanceRegisterResponseRow[]
): GetAcceptancesQuery {
  return {
    acceptance: trpcData,
  };
}

type InternalArgs = {
  entityIds?: string[];
};

const useGetAcceptancesRegisterBase = createQueryHook<
  InternalArgs,
  AcceptanceRegisterResponseRow[],
  GetAcceptancesQuery
>({
  trpcQueryOptions: (trpc, args) =>
    trpc.frontend.acceptance.register.queryOptions({
      entityIds: args.entityIds,
    }),
  mapTrpcDataToGraphQL: mapTrpcAcceptancesToGraphQL,
  graphqlDocument: GetAcceptancesDocument,
  graphqlVariables: useAcceptancesGraphqlVariables,
});

type UseGetAcceptancesRegisterArgs = Record<string, never>;

/**
 * Fetches the acceptances register with automatic entity filtering for both
 * GraphQL and tRPC paths. Entity filtering uses useEntityFilter context
 * internally — consumers don't need to pass entityIds.
 */
export const useGetAcceptancesRegister = (params: {
  queryArgs: UseGetAcceptancesRegisterArgs;
  shouldSkip?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetAcceptancesRegisterBase({
    queryArgs: {
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
  });
};
